import type { IAuthService } from './IAuthService';
import { SuperTokensAuthService } from './SuperTokensAuthService';
import { MockAuthService } from './MockAuthService';

/**
 * Environment types for auth service selection
 */
export type AuthEnvironment = 'production' | 'development' | 'test' | 'mock';

/**
 * Factory class for creating authentication service instances
 * Supports environment-based switching between different implementations
 */
export class AuthServiceFactory {
  /**
   * Get an authentication service instance
   * Creates a new instance each time for better testability
   */
  static getInstance(environment?: AuthEnvironment): IAuthService {
    const env = environment || this.detectEnvironment();
    return this.createAuthService(env);
  }

  /**
   * Create a specific auth service instance based on environment
   */
  private static createAuthService(environment: AuthEnvironment): IAuthService {
    switch (environment) {
      case 'mock':
      case 'test':
        return new MockAuthService();

      case 'production':
      case 'development':
      default:
        return new SuperTokensAuthService();
    }
  }

  /**
   * Detect the current environment based on various indicators
   */
  private static detectEnvironment(): AuthEnvironment {
    // Check for explicit environment variable
    if (typeof process !== 'undefined' && process.env) {
      const nodeEnv = process.env.NODE_ENV;
      const authEnv = process.env.VITE_AUTH_SERVICE || process.env.REACT_APP_AUTH_SERVICE;

      // Explicit auth service override
      if (authEnv === 'mock') return 'mock';
      if (authEnv === 'supertokens') return 'production';

      // Node environment detection
      if (nodeEnv === 'test') return 'test';
      if (nodeEnv === 'production') return 'production';
      if (nodeEnv === 'development') return 'development';
    }

    // Check for Vite environment variables (client-side)
    // Only check if we're in a browser environment or have access to import.meta
    try {
      if (typeof import.meta !== 'undefined' && import.meta.env) {
        const mode = import.meta.env.MODE;
        const authService = import.meta.env.VITE_AUTH_SERVICE;

        // Explicit auth service override
        if (authService === 'mock') return 'mock';
        if (authService === 'supertokens') return 'production';

        // Vite mode detection
        if (mode === 'test') return 'test';
        if (mode === 'production') return 'production';
        if (mode === 'development') return 'development';
      }
    } catch {
      // Ignore errors when import.meta is not available or accessible
      // This can happen in test environments where process is undefined
    }

    // Check for testing environment indicators
    if (typeof global !== 'undefined') {
      const globalObj = global as Record<string, unknown>;
      if (globalObj.describe || globalObj.it || globalObj.test || globalObj.jest) {
        return 'test';
      }
    }

    // Check for Vitest
    if (typeof globalThis !== 'undefined') {
      const globalThisObj = globalThis as Record<string, unknown>;
      if (globalThisObj.describe || globalThisObj.it || globalThisObj.test || globalThisObj.vi) {
        return 'test';
      }
    }

    // Check for MSW (Mock Service Worker) - indicates testing/mocking
    if (typeof window !== 'undefined') {
      const windowObj = window as unknown as Record<string, unknown>;
      if (windowObj.__msw) {
        return 'mock';
      }
    }

    // Default to development for unknown environments
    return 'development';
  }

  /**
   * Force a specific environment (useful for testing)
   */
  static forceEnvironment(environment: AuthEnvironment): IAuthService {
    return this.createAuthService(environment);
  }

  /**
   * Reset the factory (useful for testing)
   * Note: No longer needed since we don't maintain state
   */
  static reset(): void {
    // No-op: Factory is now stateless
  }

  /**
   * Get the current environment
   */
  static getCurrentEnvironment(): AuthEnvironment | null {
    return this.detectEnvironment();
  }

  /**
   * Check if currently using mock service
   */
  static isMockService(): boolean {
    const env = this.detectEnvironment();
    return env === 'mock' || env === 'test';
  }

  /**
   * Create a mock service instance directly (for testing)
   */
  static createMockService(): MockAuthService {
    return new MockAuthService();
  }

  /**
   * Create a SuperTokens service instance directly
   */
  static createSuperTokensService(): SuperTokensAuthService {
    return new SuperTokensAuthService();
  }
}

/**
 * Convenience function to get the current auth service instance
 */
export function getAuthService(environment?: AuthEnvironment): IAuthService {
  return AuthServiceFactory.getInstance(environment);
}

/**
 * Convenience function to get a mock auth service (for testing)
 */
export function getMockAuthService(): MockAuthService {
  return AuthServiceFactory.createMockService();
}